import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Building2, Users, FileText, Plus, Settings, Eye, Pause, Play, Trash2, Home, LogOut, ChevronDown, Calendar, UserPlus, AlertTriangle, CreditCard, ArrowUpCircle, ArrowDownCircle, RefreshCw, Megaphone, Bell, Edit, X, Mail, Ship, Package, Truck, DollarSign, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminDashboard() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [showFeaturesDialog, setShowFeaturesDialog] = useState(false);
  const [showTrialDialog, setShowTrialDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [trialDays, setTrialDays] = useState(30);
  const [demoAccessLogs, setDemoAccessLogs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);
  const [subscriptionUpdate, setSubscriptionUpdate] = useState({
    tier: 'basic',
    billing_cycle: 'monthly',
    status: 'active',
    subscription_ends_at: '',
    price_override: '',
    notes: ''
  });
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  const [newDemoUser, setNewDemoUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'demo',
    is_demo: true
  });
  const [newCompany, setNewCompany] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    vat_number: ''
  });
  const [systemInfo, setSystemInfo] = useState(null);
  const [subscriptionPayments, setSubscriptionPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [stripeSessionId, setStripeSessionId] = useState('');
  const [activatingSession, setActivatingSession] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(null);
  
  // Announcement states
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    priority: 'normal',
    expires_at: ''
  });
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  // Designer Service pricing states
  const [designerPricing, setDesignerPricing] = useState({ artwork_fee: 25, amendment_fee: 25, free_changes: 2 });
  const [freeCompanies, setFreeCompanies] = useState([]);
  const [designerPricingLoaded, setDesignerPricingLoaded] = useState(false);
  const [savingDesignerPricing, setSavingDesignerPricing] = useState(false);

  // Platform-wide pricing (additional branch cost etc.)
  const [platformPricing, setPlatformPricing] = useState({ additional_branch_price: 5.00 });
  const [platformPricingLoaded, setPlatformPricingLoaded] = useState(false);
  const [savingPlatformPricing, setSavingPlatformPricing] = useState(false);
  
  // Email Templates states
  const [defaultEmailTemplates, setDefaultEmailTemplates] = useState([]);
  const [myEmailTemplates, setMyEmailTemplates] = useState([]);
  const [emailTemplateVariables, setEmailTemplateVariables] = useState({});
  const [showEmailTemplateDialog, setShowEmailTemplateDialog] = useState(false);
  const [currentEmailTemplate, setCurrentEmailTemplate] = useState(null);
  const [emailTemplateForm, setEmailTemplateForm] = useState({
    template_type: '',
    subject: '',
    body: '',
    status_trigger: ''
  });
  const [isEditingDefaultTemplate, setIsEditingDefaultTemplate] = useState(false);
  const [companyBranding, setCompanyBranding] = useState({
    logo_url: '',
    company_name: 'Set In Stone Didcot Ltd',
    company_address: '',
    company_phone: '',
    company_email: 'memorials@setinstonedidcot.co.uk',
    tagline: 'Handcrafted Memorials'
  });
  
  // China Ordering states
  const [chinaOrders, setChinaOrders] = useState([]);
  const [pendingChinaOrdersCount, setPendingChinaOrdersCount] = useState(0);
  const [showChinaQuoteDialog, setShowChinaQuoteDialog] = useState(false);
  const [selectedChinaOrder, setSelectedChinaOrder] = useState(null);
  const [chinaQuoteForm, setChinaQuoteForm] = useState({
    china_cost: '',
    markup_amount: '',
    shipping_cost: '',
    quote_notes: ''
  });
  const [submittingChinaQuote, setSubmittingChinaQuote] = useState(false);
  
  const fetchAllUsers = async () => {
    try {
      setLoadingAllUsers(true);
      const res = await axios.get(`${BACKEND_URL}/api/admin/all-users`, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(res.data || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoadingAllUsers(false);
    }
  };

  const deleteAnyUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? Their email will become available again.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/users/${u.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted ${u.email}`);
      setAllUsers(prev => prev.filter(x => x.id !== u.id));
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete user');
    }
  };

  const handleActivateFromStripe = async () => {
    if (!stripeSessionId.trim()) {
      toast.error('Please enter a Stripe session ID');
      return;
    }
    
    try {
      setActivatingSession(true);
      const res = await axios.post(
        `${BACKEND_URL}/api/subscriptions/admin/activate-from-stripe?session_id=${encodeURIComponent(stripeSessionId)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Subscription activated successfully');
      setStripeSessionId('');
      fetchData();
      fetchSubscriptionPayments();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to activate subscription'); toast.error(msg);
    } finally {
      setActivatingSession(false);
    }
  };
  
  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
    fetchSystemInfo();
    fetchDemoAccessLogs();
    fetchSubscriptionPayments();
    fetchAnnouncements();
    fetchDefaultEmailTemplates();
    fetchMyEmailTemplates();
    fetchChinaOrders();
    fetchDesignerPricing();
    fetchPlatformPricing();
  }, [user, navigate]);

  const fetchPlatformPricing = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/platform-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlatformPricing({
        additional_branch_price: Number(data.additional_branch_price ?? 5),
      });
      setPlatformPricingLoaded(true);
    } catch (error) {
      console.error('Failed to fetch platform pricing:', error);
      setPlatformPricingLoaded(true);
    }
  };

  const savePlatformPricing = async () => {
    const { additional_branch_price } = platformPricing;
    if (additional_branch_price < 0) {
      toast.error('Price cannot be negative');
      return;
    }
    setSavingPlatformPricing(true);
    try {
      await axios.put(`${BACKEND_URL}/api/platform-settings`, {
        additional_branch_price: Number(additional_branch_price),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Platform pricing updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update platform pricing');
    } finally {
      setSavingPlatformPricing(false);
    }
  };

  const fetchDesignerPricing = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/designer-service/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDesignerPricing({
        artwork_fee: Number(data.artwork_fee ?? data.fee_amount ?? 25),
        amendment_fee: Number(data.amendment_fee ?? data.artwork_fee ?? 25),
        free_changes: Number(data.free_changes ?? 2),
      });
      setDesignerPricingLoaded(true);
    } catch (error) {
      console.error('Failed to fetch designer pricing:', error);
      setDesignerPricingLoaded(true);
    }
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/designer-service/free-companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFreeCompanies(data.companies || []);
    } catch (e) { /* silent */ }
  };

  const toggleCompanyFree = async (companyId, free) => {
    setFreeCompanies((cs) => cs.map((c) => c.id === companyId ? { ...c, free } : c));
    try {
      await axios.put(`${BACKEND_URL}/api/designer-service/company/${companyId}/free`, { free },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(free ? 'Designer service is now free for this company' : 'Designer service is now chargeable for this company');
    } catch (e) {
      toast.error('Failed to update — reverting');
      setFreeCompanies((cs) => cs.map((c) => c.id === companyId ? { ...c, free: !free } : c));
    }
  };

  const saveDesignerPricing = async () => {
    const { artwork_fee, amendment_fee, free_changes } = designerPricing;
    if (artwork_fee < 0 || amendment_fee < 0 || free_changes < 0) {
      toast.error('Prices and free-change count cannot be negative');
      return;
    }
    setSavingDesignerPricing(true);
    try {
      await axios.put(`${BACKEND_URL}/api/designer-service/config`, {
        artwork_fee: Number(artwork_fee),
        amendment_fee: Number(amendment_fee),
        free_changes: Number(free_changes),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Designer service pricing updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingDesignerPricing(false);
    }
  };
  
  const fetchChinaOrders = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/group-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChinaOrders(res.data || []);
      const pending = (res.data || []).filter(o => o.status === 'submitted').length;
      setPendingChinaOrdersCount(pending);
    } catch (error) {
      console.error('Failed to fetch trade wholesale orders:', error);
    }
  };
  
  const fetchSubscriptionPayments = async () => {
    try {
      setLoadingPayments(true);
      const res = await axios.get(`${BACKEND_URL}/api/subscriptions/payments/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status_filter: paymentFilter !== 'all' ? paymentFilter : undefined }
      });
      setSubscriptionPayments(res.data.transactions || []);
      setPaymentStats(res.data.stats);
    } catch (error) {
      console.error('Failed to fetch subscription payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };
  
  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchSubscriptionPayments();
    }
  }, [paymentFilter]);
  
  const openSubscriptionDialog = (company) => {
    setSelectedCompany(company);
    const sub = company.subscription || {};
    setSubscriptionUpdate({
      tier: sub.tier || 'basic',
      billing_cycle: sub.billing_cycle || 'monthly',
      status: company.status || 'active',
      subscription_ends_at: sub.subscription_ends_at ? sub.subscription_ends_at.split('T')[0] : '',
      price_override: '',
      notes: ''
    });
    setShowSubscriptionDialog(true);
  };
  
  const handleSubscriptionUpdate = async () => {
    if (!selectedCompany) return;
    
    setUpdatingSubscription(true);
    try {
      const payload = {
        tier: subscriptionUpdate.tier,
        billing_cycle: subscriptionUpdate.billing_cycle,
        status: subscriptionUpdate.status,
        notes: subscriptionUpdate.notes || undefined
      };
      
      // Only include optional fields if they have values
      if (subscriptionUpdate.subscription_ends_at) {
        payload.subscription_ends_at = new Date(subscriptionUpdate.subscription_ends_at).toISOString();
      }
      if (subscriptionUpdate.price_override && subscriptionUpdate.price_override !== '') {
        payload.price_override = parseFloat(subscriptionUpdate.price_override);
      }
      
      await axios.put(
        `${BACKEND_URL}/api/subscriptions/admin/${selectedCompany.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(`Subscription updated to ${subscriptionUpdate.tier}`);
      setShowSubscriptionDialog(false);
      fetchData(); // Refresh companies list
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update subscription'); toast.error(msg);
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleMarkSubscriptionPaid = async (company) => {
    // One-click action: extends the company's subscription by one billing cycle
    // from NOW and clears the overdue paywall. Used when a paying client gets
    // stuck on the overdue popup (Stripe webhook missed, manual payment, etc.).
    const cycle = company.subscription?.billing_cycle || company.billing_cycle || 'monthly';
    const days = cycle === 'yearly' ? 365 : 30;
    if (!window.confirm(`Mark ${company.name}'s subscription as PAID?\n\nThis extends their subscription by ${days} days from today and clears the overdue paywall.`)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/subscriptions/admin/${company.id}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || `Extended by ${days} days`);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to mark subscription paid');
    }
  };
  
  const fetchDemoAccessLogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/demo-access-logs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDemoAccessLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching demo access logs:', error);
    }
  };
  
  const fetchSystemInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/system/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemInfo(response.data);
    } catch (error) {
      console.error('Error fetching system info:', error);
    }
  };

  // Announcement functions
  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.message) {
      toast.error('Title and message are required');
      return;
    }
    
    try {
      const payload = {
        title: announcementForm.title,
        message: announcementForm.message,
        priority: announcementForm.priority,
        expires_at: announcementForm.expires_at || null
      };
      
      if (editingAnnouncement) {
        await axios.put(`${BACKEND_URL}/api/announcements/${editingAnnouncement.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Announcement updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/announcements`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Announcement created and sent to all companies');
      }
      
      setShowAnnouncementDialog(false);
      setAnnouncementForm({ title: '', message: '', priority: 'normal', expires_at: '' });
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save announcement'); toast.error(msg);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement? Users who haven\'t seen it yet won\'t receive it.')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleToggleAnnouncement = async (announcement) => {
    try {
      await axios.put(`${BACKEND_URL}/api/announcements/${announcement.id}`, {
        is_active: !announcement.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(announcement.is_active ? 'Announcement deactivated' : 'Announcement activated');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const openEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      message: announcement.message,
      priority: announcement.priority,
      expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : ''
    });
    setShowAnnouncementDialog(true);
  };

  // China Order functions
  const openChinaQuoteDialog = (order) => {
    setSelectedChinaOrder(order);
    setChinaQuoteForm({
      china_cost: order.china_cost || '',
      markup_amount: order.markup_amount || '',
      shipping_cost: order.shipping_cost || '',
      quote_notes: order.quote_notes || ''
    });
    setShowChinaQuoteDialog(true);
  };

  const handleSendChinaQuote = async () => {
    if (!chinaQuoteForm.china_cost || !chinaQuoteForm.markup_amount) {
      toast.error('Please enter supplier cost and markup amount');
      return;
    }
    
    setSubmittingChinaQuote(true);
    try {
      await axios.post(`${BACKEND_URL}/api/group-orders/${selectedChinaOrder.id}/quote`, {
        china_cost: parseFloat(chinaQuoteForm.china_cost),
        markup_amount: parseFloat(chinaQuoteForm.markup_amount),
        shipping_cost: parseFloat(chinaQuoteForm.shipping_cost) || 0,
        quote_notes: chinaQuoteForm.quote_notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Quote sent successfully');
      setShowChinaQuoteDialog(false);
      fetchChinaOrders();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send quote'); toast.error(msg);
    } finally {
      setSubmittingChinaQuote(false);
    }
  };

  const handleUpdateChinaOrderStatus = async (orderId, newStatus, additionalData = {}) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/group-orders/${orderId}`, {
        status: newStatus,
        ...additionalData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order updated');
      fetchChinaOrders();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update order'); toast.error(msg);
    }
  };

  const handleCancelChinaOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/group-orders/${orderId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order cancelled successfully');
      fetchChinaOrders();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to cancel order'); toast.error(msg);
    }
  };

  // Email Template functions
  const fetchDefaultEmailTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/email-templates/defaults`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultEmailTemplates(response.data.templates || []);
      setEmailTemplateVariables(response.data.variables || {});
    } catch (error) {
      console.error('Failed to fetch default email templates:', error);
    }
  };

  const fetchMyEmailTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch my email templates:', error);
    }
  };

  const openEmailTemplateEditor = (template, isDefault = false) => {
    setCurrentEmailTemplate(template);
    setIsEditingDefaultTemplate(isDefault);
    setEmailTemplateForm({
      template_type: template.template_type,
      subject: template.subject,
      body: template.body,
      status_trigger: template.status_trigger || ''
    });
    setShowEmailTemplateDialog(true);
  };

  const saveEmailTemplate = async () => {
    try {
      const endpoint = isEditingDefaultTemplate 
        ? `${BACKEND_URL}/api/email-templates/defaults`
        : `${BACKEND_URL}/api/email-templates`;
      
      await axios.post(endpoint, emailTemplateForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isEditingDefaultTemplate ? 'Default template updated' : 'Template saved');
      setShowEmailTemplateDialog(false);
      
      if (isEditingDefaultTemplate) {
        fetchDefaultEmailTemplates();
      } else {
        fetchMyEmailTemplates();
      }
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save template'); toast.error(msg);
    }
  };

  const getTemplateTypeLabel = (type) => {
    const labels = {
      quote: 'Quote Email',
      invoice: 'Invoice Email',
      artwork: 'Artwork Approval',
      artwork_changes: 'Artwork Changes',
      status_change: 'Status Update',
      completion: 'Order Completion',
      contact_client: 'Contact Client'
    };
    return labels[type] || type;
  };

  // Generate live preview HTML for email template
  const generateEmailPreview = () => {
    const sampleData = {
      client_name: 'John Smith',
      order_number: 'ORD-2024-001',
      total_amount: '1,250.00',
      invoice_amount: '1,250.00',
      amount_paid: '500.00',
      balance_due: '750.00',
      quote_date: new Date().toLocaleDateString('en-GB'),
      invoice_date: new Date().toLocaleDateString('en-GB'),
      memorial_type: 'Headstone',
      artwork_link: 'https://example.com/artwork',
      version_number: '2',
      change_notes: 'Updated inscription text',
      new_status: 'In Production',
      old_status: 'Approved',
      status_message: 'Your memorial is now being crafted.',
      completion_details: 'Ready for collection or delivery.',
      completion_date: new Date().toLocaleDateString('en-GB'),
      company_name: companyBranding.company_name || 'Your Company',
      company_phone: companyBranding.company_phone || '01onal 234567',
      company_email: companyBranding.company_email || 'info@company.com',
      bank_details: 'Sort Code: 12-34-56\\nAccount: 12345678'
    };

    let subject = emailTemplateForm.subject;
    let body = emailTemplateForm.body;

    Object.entries(sampleData).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      subject = subject.replace(regex, value);
      body = body.replace(regex, value);
    });

    const htmlBody = body.replace(/\n/g, '<br>');
    return { subject, body: htmlBody };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, companiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/companies/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/companies/`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCompany = async () => {
    if (!newCompany.name || !newCompany.email) {
      toast.error('Company name and email are required');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/companies/`, newCompany, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company created successfully');
      setShowCreateDialog(false);
      setNewCompany({ name: '', email: '', phone: '', address: '', website: '', vat_number: '' });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create company'); toast.error(msg);
    }
  };
  
  const handleSuspendCompany = async (companyId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/companies/${companyId}/suspend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company suspended');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to suspend company'); toast.error(msg);
    }
  };
  
  const handleActivateCompany = async (companyId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/companies/${companyId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company activated');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to activate company'); toast.error(msg);
    }
  };
  
  const handleEnableTrial = async () => {
    if (!selectedCompany) return;
    try {
      await axios.post(`${BACKEND_URL}/api/companies/${selectedCompany.id}/enable-trial`, 
        { days: trialDays },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Trial enabled for ${trialDays} days`);
      setShowTrialDialog(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to enable trial'); toast.error(msg);
    }
  };
  
  const handleDisableTrial = async (companyId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/companies/${companyId}/disable-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trial disabled');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to disable trial'); toast.error(msg);
    }
  };
  
  const handleCreateDemoUser = async () => {
    if (!selectedCompany || !newDemoUser.name || !newDemoUser.email || !newDemoUser.password) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/companies/${selectedCompany.id}/users?send_welcome_email=false`,
        newDemoUser,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Demo user created successfully');
      setShowCreateUserDialog(false);
      setNewDemoUser({ name: '', email: '', password: '', role: 'demo', is_demo: true });
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create demo user'); toast.error(msg);
    }
  };
  
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };
  
  const handleToggleFeature = async (companyId, feature, currentValue) => {
    // Update UI immediately for instant feedback (no flicker)
    setSelectedCompany(prev => ({
      ...prev,
      features: {
        ...prev?.features,
        [feature]: !currentValue
      }
    }));
    
    // Also update the companies list
    setCompanies(prev => prev.map(c => 
      c.id === companyId 
        ? { ...c, features: { ...c.features, [feature]: !currentValue } }
        : c
    ));
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/companies/${companyId}/toggle-feature?feature=${feature}&enabled=${!currentValue}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${feature.replace('_enabled', '').replace(/_/g, ' ')} ${!currentValue ? 'enabled' : 'disabled'}`, {
        duration: 1500
      });
    } catch (error) {
      // Revert on error
      setSelectedCompany(prev => ({
        ...prev,
        features: {
          ...prev?.features,
          [feature]: currentValue
        }
      }));
      setCompanies(prev => prev.map(c => 
        c.id === companyId 
          ? { ...c, features: { ...c.features, [feature]: currentValue } }
          : c
      ));
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to toggle feature'); toast.error(msg);
    }
  };
  
  const handleToggleAllFeatures = async (enableAll) => {
    if (!selectedCompany) return;
    
    const allFeatures = [
      'worktops_enabled',
      'stock_management_enabled', 
      'artwork_tools_enabled',
      'email_sending_enabled',
      'white_label_enabled',
      'grave_vaults_enabled',
      'corel_integration_enabled'
    ];
    
    // Update UI immediately
    const newFeatures = {};
    allFeatures.forEach(f => { newFeatures[f] = enableAll; });
    
    setSelectedCompany(prev => ({
      ...prev,
      features: newFeatures
    }));
    
    setCompanies(prev => prev.map(c => 
      c.id === selectedCompany.id 
        ? { ...c, features: newFeatures }
        : c
    ));
    
    // Send all toggle requests
    try {
      await Promise.all(
        allFeatures.map(feature =>
          axios.post(
            `${BACKEND_URL}/api/companies/${selectedCompany.id}/toggle-feature?feature=${feature}&enabled=${enableAll}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toast.success(`All features ${enableAll ? 'enabled' : 'disabled'}`, { duration: 1500 });
    } catch (error) {
      // Revert on error - refetch to get correct state
      fetchData();
      toast.error('Failed to toggle all features');
    }
  };
  
  const handleDeleteCompany = async () => {
    if (!selectedCompany) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/companies/${selectedCompany.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Company "${selectedCompany.name}" and all its data deleted permanently`);
      setShowDeleteDialog(false);
      setDeleteStep(1);
      setSelectedCompany(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to delete company'); toast.error(msg);
    }
  };
  
  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      trial: 'bg-blue-100 text-blue-800',
      suspended: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={variants[status] || variants.active}>{status}</Badge>;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-lg text-white">Loading...</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Building2 className="h-8 w-8 text-blue-400" />
              <div>
                <h1 className="text-xl font-bold text-white">Memorial Desk Platform Admin</h1>
                <p className="text-xs text-slate-400">Multi-Tenant Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-700">
                  <Home className="h-4 w-4 mr-2" />
                  Main Dashboard
                </Button>
              </Link>
              <Button variant="ghost" onClick={logout} className="text-slate-300 hover:text-white hover:bg-slate-700">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Total Companies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.companies?.total || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                {stats?.companies?.trial || 0} on trial
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Active Companies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">{stats?.companies?.active || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                Paying & trial accounts
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">{stats?.users?.total || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                Across all companies
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Total Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-400">
                {(stats?.orders?.memorials || 0) + (stats?.orders?.worktops || 0)}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {stats?.orders?.memorials || 0} memorials, {stats?.orders?.worktops || 0} worktops
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* System Info Card */}
        {systemInfo && (
          <Card className="bg-slate-800/50 border-slate-700 mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                <Settings className="h-4 w-4" />
                System Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Database Name:</span>
                  <span className="ml-2 text-white font-mono bg-slate-700 px-2 py-1 rounded" data-testid="db-name">
                    {systemInfo.database_name}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Database Host:</span>
                  <span className="ml-2 text-slate-300 font-mono">
                    {systemInfo.database_host}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Environment:</span>
                  <span className="ml-2 text-green-400">
                    {systemInfo.database_host === 'localhost:27017' ? 'Preview' : 'Production'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Demo Access Logs */}
        {demoAccessLogs.length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700 mb-8">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Recent Demo Access ({demoAccessLogs.length})
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchDemoAccessLogs}
                  className="text-slate-400 hover:text-white"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {demoAccessLogs.map((log, idx) => (
                  <div key={log.id || idx} className="flex items-center justify-between text-sm p-2 bg-slate-700/50 rounded">
                    <div className="flex items-center gap-3">
                      <span className="text-green-400">●</span>
                      <span className="text-slate-300">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <span className="text-slate-500 text-xs font-mono">
                      {log.ip_address}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* China Ordering Section */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <Ship className="h-5 w-5 text-cyan-400" />
                Trade Wholesale Quote Requests
                {pendingChinaOrdersCount > 0 && (
                  <Badge className="bg-red-500 text-white ml-2">{pendingChinaOrdersCount} Pending</Badge>
                )}
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={fetchChinaOrders}
                className="text-slate-400 hover:text-white"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {chinaOrders.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No group orders yet</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {chinaOrders.map(order => (
                  <div key={order.id} className={`p-4 rounded-lg border ${
                    order.status === 'submitted' ? 'bg-blue-900/20 border-blue-500/50' :
                    order.status === 'quoted' ? 'bg-purple-900/20 border-purple-500/50' :
                    order.status === 'accepted' ? 'bg-green-900/20 border-green-500/50' :
                    'bg-slate-700/50 border-slate-600'
                  }`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-mono font-semibold text-white">{order.reference_number}</p>
                        <p className="text-sm text-slate-400">{order.company_name}</p>
                      </div>
                      <Badge className={
                        order.status === 'submitted' ? 'bg-blue-600' :
                        order.status === 'quoted' ? 'bg-purple-600' :
                        order.status === 'accepted' ? 'bg-green-600' :
                        order.status === 'ordered' ? 'bg-orange-600' :
                        order.status === 'shipped' ? 'bg-cyan-600' :
                        order.status === 'arrived' ? 'bg-emerald-600' :
                        order.status === 'collected' ? 'bg-slate-600' :
                        'bg-slate-600'
                      }>
                        {order.status.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-slate-300 mb-2">
                      <span className="text-slate-400">Stone:</span> {order.stone_colour} {order.stone_type}
                      <span className="mx-2">|</span>
                      <span className="text-slate-400">Components:</span> {order.components?.length || 0}
                    </div>
                    
                    {order.quote_amount && (
                      <div className="text-sm text-green-400 mb-2">
                        Quote: £{parseFloat(order.quote_amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        {order.deposit_paid && <span className="ml-2 text-green-300">(Deposit Paid)</span>}
                      </div>
                    )}
                    
                    {order.eta && (
                      <div className="text-sm text-orange-400 mb-2">
                        ETA: {new Date(order.eta).toLocaleDateString('en-GB')}
                      </div>
                    )}
                    
                    <div className="flex gap-2 mt-3">
                      {order.status === 'submitted' && (
                        <Button 
                          size="sm" 
                          onClick={() => openChinaQuoteDialog(order)}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Send Quote
                        </Button>
                      )}
                      {order.status === 'quoted' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openChinaQuoteDialog(order)}
                          className="text-purple-400 border-purple-500 hover:bg-purple-900/50"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit Quote
                        </Button>
                      )}
                      {order.status === 'accepted' && !order.deposit_paid && (
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateChinaOrderStatus(order.id, 'ordered', { deposit_paid: true })}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Mark Deposit Paid
                        </Button>
                      )}
                      {order.status === 'ordered' && (
                        <Button 
                          size="sm" 
                          onClick={() => {
                            const eta = prompt('Enter ETA (YYYY-MM-DD):');
                            if (eta) handleUpdateChinaOrderStatus(order.id, 'shipped', { eta });
                          }}
                          className="bg-cyan-600 hover:bg-cyan-700"
                        >
                          <Ship className="w-4 h-4 mr-1" />
                          Mark Shipped
                        </Button>
                      )}
                      {order.status === 'shipped' && (
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateChinaOrderStatus(order.id, 'arrived')}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Package className="w-4 h-4 mr-1" />
                          Mark Arrived
                        </Button>
                      )}
                      {order.status === 'arrived' && !order.balance_paid && (
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateChinaOrderStatus(order.id, 'arrived', { balance_paid: true })}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Mark Balance Paid
                        </Button>
                      )}
                      {order.status === 'arrived' && order.balance_paid && (
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateChinaOrderStatus(order.id, 'ready_for_collection')}
                          className="bg-lime-600 hover:bg-lime-700"
                        >
                          <Truck className="w-4 h-4 mr-1" />
                          Ready for Collection
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openChinaQuoteDialog(order)}
                        className="text-slate-400 border-slate-500 hover:bg-slate-700"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                      {/* Cancel button - admins can cancel at any point except collected */}
                      {order.status !== 'cancelled' && order.status !== 'collected' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleCancelChinaOrder(order.id)}
                          className="text-red-400 border-red-500 hover:bg-red-900/30"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Platform Announcements Section */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-amber-400" />
                Platform Announcements
              </CardTitle>
              <Button 
                onClick={() => {
                  setEditingAnnouncement(null);
                  setAnnouncementForm({ title: '', message: '', priority: 'normal', expires_at: '' });
                  setShowAnnouncementDialog(true);
                }}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Announcement
              </Button>
            </div>
            <CardDescription className="text-slate-400">
              Send popup notifications to all companies (excluding demo accounts)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {announcements.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No announcements yet. Create one to notify all companies.</p>
            ) : (
              <div className="space-y-3">
                {announcements.map((announcement) => (
                  <div 
                    key={announcement.id} 
                    className={`p-4 rounded-lg border ${
                      announcement.is_active 
                        ? 'bg-slate-700/50 border-slate-600' 
                        : 'bg-slate-800/50 border-slate-700 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-medium">{announcement.title}</h4>
                          <Badge className={
                            announcement.priority === 'urgent' ? 'bg-red-600' :
                            announcement.priority === 'important' ? 'bg-amber-600' : 'bg-blue-600'
                          }>
                            {announcement.priority}
                          </Badge>
                          {!announcement.is_active && (
                            <Badge variant="outline" className="border-slate-500 text-slate-400">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-slate-400 text-sm line-clamp-2">{announcement.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>Created: {new Date(announcement.created_at).toLocaleDateString()}</span>
                          {announcement.expires_at && (
                            <span>Expires: {new Date(announcement.expires_at).toLocaleDateString()}</span>
                          )}
                          <span>By: {announcement.created_by}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleAnnouncement(announcement)}
                          className="text-slate-400 hover:text-white"
                          title={announcement.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {announcement.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditAnnouncement(announcement)}
                          className="text-slate-400 hover:text-white"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Designer Service Pricing Section */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8" data-testid="designer-pricing-card">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-fuchsia-400" />
              Designer Service Pricing
            </CardTitle>
            <CardDescription className="text-slate-400">
              Set what companies pay for platform-designed memorial artwork, and how many free amendments are included.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!designerPricingLoaded ? (
              <p className="text-slate-500 text-sm">Loading pricing…</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Artwork fee (£)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={designerPricing.artwork_fee}
                    onChange={(e) => setDesignerPricing({ ...designerPricing, artwork_fee: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="designer-artwork-fee-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Charged when a mason first requests artwork.</p>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Amendment fee (£)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={designerPricing.amendment_fee}
                    onChange={(e) => setDesignerPricing({ ...designerPricing, amendment_fee: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="designer-amendment-fee-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Charged for each amendment after the free allowance.</p>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Free amendments included</label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={designerPricing.free_changes}
                    onChange={(e) => setDesignerPricing({ ...designerPricing, free_changes: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="designer-free-changes-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Amendments before the mason is charged again.</p>
                </div>
              </div>
            )}
            {designerPricingLoaded && (
              <div className="mt-6 border-t border-slate-700 pt-4" data-testid="free-companies-section">
                <label className="block text-sm text-slate-300 mb-1">Free for internal use</label>
                <p className="text-xs text-slate-500 mb-3">Companies toggled on here get the designer service free (no Stripe charge) — for your own / internal businesses.</p>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {freeCompanies.map((c) => (
                    <label key={c.id} className="flex items-center justify-between gap-3 bg-slate-900 border border-slate-700 rounded px-3 py-2 cursor-pointer" data-testid={`free-company-row-${c.id}`}>
                      <span className="text-sm text-slate-200 truncate">{c.name}</span>
                      <input type="checkbox" checked={!!c.free}
                        onChange={(e) => toggleCompanyFree(c.id, e.target.checked)}
                        data-testid={`free-company-toggle-${c.id}`} />
                    </label>
                  ))}
                  {freeCompanies.length === 0 && <p className="text-xs text-slate-500">No companies found.</p>}
                </div>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button
                onClick={saveDesignerPricing}
                disabled={savingDesignerPricing || !designerPricingLoaded}
                className="bg-fuchsia-600 hover:bg-fuchsia-700"
                data-testid="designer-pricing-save-btn"
              >
                {savingDesignerPricing ? 'Saving…' : 'Save Pricing'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Platform Pricing (Additional Branches etc.) */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8" data-testid="platform-pricing-card">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-400" />
              Platform Pricing
            </CardTitle>
            <CardDescription className="text-slate-400">
              Global pricing that applies across every customer company on the platform.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!platformPricingLoaded ? (
              <p className="text-slate-500 text-sm">Loading pricing…</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Additional branch price (£/month per branch)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={platformPricing.additional_branch_price}
                    onChange={(e) => setPlatformPricing({ ...platformPricing, additional_branch_price: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="additional-branch-price-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Charged when a Standard-plan company adds branches beyond their included limit.</p>
                </div>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button
                onClick={savePlatformPricing}
                disabled={savingPlatformPricing || !platformPricingLoaded}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="platform-pricing-save-btn"
              >
                {savingPlatformPricing ? 'Saving…' : 'Save Platform Pricing'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email Templates Section */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
              <Mail className="h-5 w-5 text-cyan-400" />
              Email Templates
            </CardTitle>
            <CardDescription className="text-slate-400">
              Manage default templates (used by all companies) and your Set in Stone templates
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Default Templates */}
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-400" />
                Default Templates (All Companies)
              </h4>
              <p className="text-slate-500 text-sm mb-3">
                These templates are used by companies without white-label subscription
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {defaultEmailTemplates.map((template) => (
                  <div 
                    key={template.template_type}
                    className="p-3 bg-slate-700/50 rounded-lg border border-slate-600 hover:border-slate-500 cursor-pointer transition-colors"
                    onClick={() => openEmailTemplateEditor(template, true)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-white font-medium text-sm">{getTemplateTypeLabel(template.template_type)}</h5>
                        <p className="text-slate-500 text-xs truncate mt-1">{template.subject}</p>
                      </div>
                      <Edit className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* My Templates (Set in Stone) */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400" />
                Set in Stone Templates
              </h4>
              <p className="text-slate-500 text-sm mb-3">
                Your custom templates that override defaults for Set in Stone
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {myEmailTemplates.map((template) => (
                  <div 
                    key={template.template_type}
                    className="p-3 bg-slate-700/50 rounded-lg border border-slate-600 hover:border-slate-500 cursor-pointer transition-colors"
                    onClick={() => openEmailTemplateEditor(template, false)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-white font-medium text-sm">{getTemplateTypeLabel(template.template_type)}</h5>
                        <p className="text-slate-500 text-xs truncate mt-1">{template.subject}</p>
                        {template.is_custom && (
                          <Badge className="bg-green-600 text-xs mt-1">Customized</Badge>
                        )}
                      </div>
                      <Edit className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Subscription Payments Section */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-400" />
                Subscription Payments
              </CardTitle>
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payments</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {paymentStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Total Revenue</p>
                  <p className="text-xl font-bold text-green-400">£{(paymentStats.total_revenue || 0).toFixed(2)}</p>
                </div>
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Successful</p>
                  <p className="text-xl font-bold text-white">{paymentStats.paid || 0}</p>
                </div>
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Pending</p>
                  <p className="text-xl font-bold text-yellow-400">{paymentStats.pending || 0}</p>
                </div>
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Failed</p>
                  <p className="text-xl font-bold text-red-400">{paymentStats.failed || 0}</p>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {loadingPayments ? (
              <div className="text-center py-4 text-slate-400">Loading payments...</div>
            ) : subscriptionPayments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No subscription payments recorded yet
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {subscriptionPayments.map((payment, idx) => (
                  <div key={payment.id || idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${
                        payment.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        payment.payment_status === 'pending' || payment.payment_status === 'initiated' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {payment.payment_status === 'paid' ? <ArrowUpCircle className="h-4 w-4" /> :
                         payment.payment_status === 'pending' || payment.payment_status === 'initiated' ? <Calendar className="h-4 w-4" /> :
                         <ArrowDownCircle className="h-4 w-4" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{payment.company_name}</p>
                        <p className="text-xs text-slate-400">
                          {payment.plan_id} • {payment.billing_cycle || 'monthly'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-white">
                        £{(payment.amount || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={`ml-4 ${
                      payment.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                      payment.payment_status === 'pending' || payment.payment_status === 'initiated' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {payment.payment_status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
            
            {/* Manual Activation Section */}
            <div className="border-t border-slate-600 pt-4 mt-4">
              <p className="text-sm text-slate-400 mb-2">Payment went through on Stripe but not reflected here?</p>
              <div className="flex gap-2">
                <Input
                  value={stripeSessionId}
                  onChange={(e) => setStripeSessionId(e.target.value)}
                  placeholder="Stripe Session ID (cs_...)"
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
                <Button 
                  onClick={handleActivateFromStripe}
                  disabled={activatingSession || !stripeSessionId.trim()}
                  className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                >
                  {activatingSession ? 'Activating...' : 'Activate Subscription'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* All Registered Users Section */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader className="cursor-pointer" onClick={() => {
            const next = !showAllUsers;
            setShowAllUsers(next);
            if (next && allUsers.length === 0) fetchAllUsers();
          }} data-testid="all-users-toggle">
            <CardTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2"><Users className="h-5 w-5" /> All Registered Users</span>
              <ChevronDown className={`h-5 w-5 transition-transform ${showAllUsers ? 'rotate-180' : ''}`} />
            </CardTitle>
            <CardDescription className="text-slate-400">
              Every user account on the platform, including ones not linked to any company. Delete stuck accounts here so their email can be re-used.
            </CardDescription>
          </CardHeader>
          {showAllUsers && (
            <CardContent>
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by email, name, role or company…"
                className="mb-4 bg-slate-900 border-slate-600 text-white"
                data-testid="all-users-search"
              />
              {loadingAllUsers ? (
                <p className="text-slate-400 text-sm py-4">Loading users…</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {allUsers
                    .filter(u => {
                      const q = userSearch.trim().toLowerCase();
                      if (!q) return true;
                      return [u.email, u.name, u.role, u.company_name].some(v => v && String(v).toLowerCase().includes(q));
                    })
                    .map(u => (
                      <div key={u.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${!u.company_id ? 'border-amber-600/60 bg-amber-900/10' : 'border-slate-700 bg-slate-900/40'}`} data-testid={`all-users-row-${u.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white truncate">{u.name || '—'} <span className="text-slate-400 font-normal">({u.email})</span></p>
                          <p className="text-xs text-slate-400">
                            <Badge variant="secondary" className="mr-2 text-[10px]">{(u.role || '?').toUpperCase()}</Badge>
                            {u.company_name || (u.company_id ? u.company_id : <span className="text-amber-400 font-medium">No company — hidden from team lists</span>)}
                          </p>
                        </div>
                        {u.id !== user?.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-700 text-red-400 hover:bg-red-900/30 shrink-0"
                            onClick={() => deleteAnyUser(u)}
                            data-testid={`delete-any-user-${u.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  {allUsers.length === 0 && <p className="text-slate-400 text-sm py-4">No users found.</p>}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Companies Section */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">Companies</h2>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        </div>
        
        <div className="grid gap-4">
          {companies.map((company) => (
            <Card key={company.id} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{company.name}</h3>
                      {getStatusBadge(company.status)}
                      {company.is_master && (
                        <Badge className="bg-purple-100 text-purple-800">Master</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-400">{company.email}</p>
                    {company.subscription?.tier === 'trial' && company.subscription?.trial_ends_at && (
                      <p className="text-xs text-yellow-400 mt-1">
                        Trial ends: {new Date(company.subscription.trial_ends_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/company-settings?company=${company.id}`)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedCompany(company);
                        setShowFeaturesDialog(true);
                      }}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Features
                    </Button>
                    
                    {!company.is_master && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openSubscriptionDialog(company)}
                          className="border-purple-600 text-purple-400 hover:bg-purple-900/20"
                          data-testid={`manage-subscription-${company.id}`}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Subscription
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMarkSubscriptionPaid(company)}
                          className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/20"
                          data-testid={`mark-paid-${company.id}`}
                          title="Extends subscription by one billing cycle and clears the overdue paywall"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Mark Paid
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedCompany(company);
                            setTrialDays(30);
                            setShowTrialDialog(true);
                          }}
                          className="border-blue-600 text-blue-400 hover:bg-blue-900/20"
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Trial
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedCompany(company);
                            setNewDemoUser({ name: '', email: '', password: generatePassword(), role: 'demo', is_demo: true });
                            setShowCreateUserDialog(true);
                          }}
                          className="border-purple-600 text-purple-400 hover:bg-purple-900/20"
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Demo User
                        </Button>
                        {company.status === 'suspended' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivateCompany(company.id)}
                            className="border-green-600 text-green-400 hover:bg-green-900/20"
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Activate
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSuspendCompany(company.id)}
                            className="border-yellow-600 text-yellow-400 hover:bg-yellow-900/20"
                          >
                            <Pause className="h-4 w-4 mr-1" />
                            Suspend
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedCompany(company);
                            setDeleteStep(1);
                            setShowDeleteDialog(true);
                          }}
                          className="border-red-600 text-red-400 hover:bg-red-900/20"
                          data-testid={`platform-company-delete-btn-${company.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Feature Badges */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {company.features?.worktops_enabled && (
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">Worktops</Badge>
                  )}
                  {company.features?.stock_management_enabled && (
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">Stock</Badge>
                  )}
                  {company.features?.artwork_tools_enabled && (
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">Artwork</Badge>
                  )}
                  {company.features?.email_sending_enabled && (
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">Emails</Badge>
                  )}
                  {company.features?.grave_vaults_enabled && (
                    <Badge variant="secondary" className="bg-orange-900/50 text-orange-300">Grave Vaults</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
      
      {/* Create Company Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new company to the platform. They will start with a 30-day trial.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-300">Company Name *</Label>
              <Input
                id="name"
                value={newCompany.name}
                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Memorial Services Ltd"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-slate-300">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newCompany.email}
                onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="contact@company.com"
              />
            </div>
            
            <div>
              <Label htmlFor="phone" className="text-slate-300">Phone</Label>
              <Input
                id="phone"
                value={newCompany.phone}
                onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="01onal 123456"
              />
            </div>
            
            <div>
              <Label htmlFor="address" className="text-slate-300">Address</Label>
              <Input
                id="address"
                value={newCompany.address}
                onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="123 High Street, Town"
              />
            </div>
            
            <div>
              <Label htmlFor="vat_number" className="text-slate-300">VAT Number</Label>
              <Input
                id="vat_number"
                value={newCompany.vat_number}
                onChange={(e) => setNewCompany({ ...newCompany, vat_number: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="GB123456789"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleCreateCompany} className="bg-blue-600 hover:bg-blue-700">
              Create Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Features Dialog */}
      <Dialog open={showFeaturesDialog} onOpenChange={setShowFeaturesDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Features: {selectedCompany?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enable or disable features for this company.
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-3">
              {/* Toggle All Buttons */}
              <div className="flex gap-2 pb-2 border-b border-slate-700">
                <Button
                  size="sm"
                  onClick={() => handleToggleAllFeatures(true)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Enable All
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleToggleAllFeatures(false)}
                  variant="outline"
                  className="flex-1 border-slate-500 text-slate-300 hover:bg-slate-700"
                >
                  Disable All
                </Button>
              </div>
              
              {[
                { key: 'worktops_enabled', label: 'Worktop Orders', description: 'Enable worktop quoting and management' },
                { key: 'stock_management_enabled', label: 'Stock Management', description: 'Track memorial stock inventory' },
                { key: 'artwork_tools_enabled', label: 'Artwork Tools', description: 'Artwork upload and approval workflow' },
                { key: 'email_sending_enabled', label: 'Email Sending', description: 'Send quotes and invoices via email' },
                { key: 'white_label_enabled', label: 'White Label', description: 'Custom branding on documents' },
                { key: 'grave_vaults_enabled', label: 'Grave Vaults', description: 'Enable grave vault order type' },
                { key: 'corel_integration_enabled', label: 'Corel Integration', description: 'Master library + plugin API (granites, shapes, motifs, text tools)' }
              ].map((feature) => {
                const isEnabled = selectedCompany.features?.[feature.key] ?? false;
                const isDisabled = selectedCompany.is_master && feature.key === 'worktops_enabled';
                
                return (
                  <div 
                    key={feature.key} 
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      isEnabled 
                        ? 'bg-green-900/20 border-green-700/50' 
                        : 'bg-slate-700/30 border-slate-600/50'
                    }`}
                  >
                    <div className="flex-1 mr-4">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white">{feature.label}</p>
                        {isEnabled && (
                          <Badge className="bg-green-600 text-white text-xs px-2 py-0">ON</Badge>
                        )}
                        {!isEnabled && (
                          <Badge className="bg-slate-600 text-slate-300 text-xs px-2 py-0">OFF</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{feature.description}</p>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => handleToggleFeature(selectedCompany.id, feature.key, isEnabled)}
                      disabled={isDisabled}
                      className={`${
                        isEnabled 
                          ? 'data-[state=checked]:bg-green-500' 
                          : 'data-[state=unchecked]:bg-slate-600'
                      } h-6 w-11`}
                    />
                  </div>
                );
              })}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeaturesDialog(false)} className="border-slate-600 text-slate-300">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Trial Management Dialog */}
      <Dialog open={showTrialDialog} onOpenChange={setShowTrialDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Manage Trial: {selectedCompany?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enable or extend a free trial period for this company.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Trial Duration (Days)</Label>
              <Select value={trialDays.toString()} onValueChange={(v) => setTrialDays(parseInt(v))}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {selectedCompany?.subscription?.tier === 'trial' && (
              <div className="p-3 bg-yellow-900/30 rounded-lg border border-yellow-700">
                <p className="text-sm text-yellow-400">
                  Current trial ends: {selectedCompany?.subscription?.trial_ends_at 
                    ? new Date(selectedCompany.subscription.trial_ends_at).toLocaleDateString() 
                    : 'N/A'}
                </p>
              </div>
            )}
            
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-400">
                Enabling trial will give the company access to <strong>all features</strong> for the duration.
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            {selectedCompany?.subscription?.tier === 'trial' && (
              <Button 
                variant="outline" 
                onClick={() => {
                  handleDisableTrial(selectedCompany.id);
                  setShowTrialDialog(false);
                }}
                className="border-red-600 text-red-400 hover:bg-red-900/20"
              >
                Disable Trial
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowTrialDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleEnableTrial} className="bg-blue-600 hover:bg-blue-700">
              Enable Trial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Demo User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Create Demo User: {selectedCompany?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a demo account with all features enabled. No emails will be sent from demo accounts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Full Name *</Label>
              <Input
                value={newDemoUser.name}
                onChange={(e) => setNewDemoUser({ ...newDemoUser, name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Demo User"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={newDemoUser.email}
                onChange={(e) => setNewDemoUser({ ...newDemoUser, email: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="demo@company.com"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Password *</Label>
              <div className="flex gap-2">
                <Input
                  value={newDemoUser.password}
                  onChange={(e) => setNewDemoUser({ ...newDemoUser, password: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <Button 
                  variant="outline" 
                  onClick={() => setNewDemoUser({ ...newDemoUser, password: generatePassword() })}
                  className="border-slate-600 text-slate-300"
                >
                  Generate
                </Button>
              </div>
            </div>
            
            <div className="p-3 bg-purple-900/30 rounded-lg border border-purple-700">
              <p className="text-sm text-purple-400">
                <strong>Demo Mode:</strong> This user will have all admin features but emails will NOT be sent. Perfect for letting prospects try the system.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUserDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleCreateDemoUser} className="bg-purple-600 hover:bg-purple-700">
              Create Demo User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Company Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(o) => { setShowDeleteDialog(o); if (!o) setDeleteStep(1); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Delete Company Permanently
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action cannot be undone. This will permanently delete the company and all associated data.
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-4">
              <div className="p-4 bg-red-900/30 rounded-lg border border-red-700">
                <p className="text-sm text-red-300 font-medium mb-2">
                  You are about to delete:
                </p>
                <p className="text-lg font-bold text-white">{selectedCompany.name}</p>
                <p className="text-xs text-red-400 mt-2">
                  This will delete all users, orders, quotes, invoices, and other data associated with this company.
                </p>
              </div>
              
              {deleteStep === 2 && (
                <div className="p-3 bg-red-600/20 border border-red-500 rounded-lg text-sm text-red-300 font-semibold" data-testid="platform-delete-final-warning">
                  Final confirmation — this cannot be undone. Click "Confirm Deletion" to permanently delete {selectedCompany.name}.
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteStep(1);
              }} 
              className="border-slate-600 text-slate-300"
              data-testid="platform-delete-cancel-btn"
            >
              Cancel
            </Button>
            {deleteStep === 1 ? (
              <Button 
                onClick={() => setDeleteStep(2)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="platform-delete-are-you-sure-btn"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Yes, I'm Sure — Continue
              </Button>
            ) : (
              <Button 
                onClick={handleDeleteCompany}
                className="bg-red-600 hover:bg-red-700"
                data-testid="platform-delete-confirm-final-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Confirm Deletion
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Subscription Management Dialog */}
      <Dialog open={showSubscriptionDialog} onOpenChange={setShowSubscriptionDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-400" />
              Manage Subscription
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update subscription tier and status for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-4">
              {/* Current Status */}
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Current Plan</p>
                <div className="flex items-center gap-2">
                  <Badge className={
                    selectedCompany.subscription?.tier === 'premium' ? 'bg-purple-600' :
                    selectedCompany.subscription?.tier === 'standard' ? 'bg-blue-600' :
                    selectedCompany.subscription?.tier === 'trial' ? 'bg-yellow-600' :
                    'bg-slate-600'
                  }>
                    {selectedCompany.subscription?.tier || 'None'}
                  </Badge>
                  <span className="text-slate-400 text-sm">
                    {selectedCompany.subscription?.monthly_price ? `£${selectedCompany.subscription.monthly_price}/mo` : ''}
                  </span>
                </div>
              </div>
              
              {/* Tier Selection */}
              <div className="space-y-2">
                <Label className="text-slate-300">Subscription Tier</Label>
                <Select 
                  value={subscriptionUpdate.tier} 
                  onValueChange={(value) => setSubscriptionUpdate({...subscriptionUpdate, tier: value})}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="trial" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                        Trial (Free - 30 days)
                      </div>
                    </SelectItem>
                    <SelectItem value="basic" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-slate-500"></span>
                        Basic - £18/mo (Yearly: 10% off)
                      </div>
                    </SelectItem>
                    <SelectItem value="standard" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        Standard - £25/mo (Yearly: 12% off)
                      </div>
                    </SelectItem>
                    <SelectItem value="premium" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                        Premium - £35/mo (Yearly: 15% off)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Status Selection */}
              <div className="space-y-2">
                <Label className="text-slate-300">Account Status</Label>
                <Select 
                  value={subscriptionUpdate.status} 
                  onValueChange={(value) => setSubscriptionUpdate({...subscriptionUpdate, status: value})}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="active" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        Active
                      </div>
                    </SelectItem>
                    <SelectItem value="trial" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        Trial
                      </div>
                    </SelectItem>
                    <SelectItem value="suspended" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                        Suspended
                      </div>
                    </SelectItem>
                    <SelectItem value="cancelled" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        Cancelled
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Billing Cycle */}
              <div className="space-y-2">
                <Label className="text-slate-300">Billing Cycle</Label>
                <Select 
                  value={subscriptionUpdate.billing_cycle} 
                  onValueChange={(value) => setSubscriptionUpdate({...subscriptionUpdate, billing_cycle: value})}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="monthly" className="text-white hover:bg-slate-700">
                      Monthly
                    </SelectItem>
                    <SelectItem value="yearly" className="text-white hover:bg-slate-700">
                      Yearly (Save 10-15%)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Subscription End Date */}
              <div className="space-y-2">
                <Label className="text-slate-300">Subscription End Date (optional)</Label>
                <Input
                  type="date"
                  value={subscriptionUpdate.subscription_ends_at}
                  onChange={(e) => setSubscriptionUpdate({...subscriptionUpdate, subscription_ends_at: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <p className="text-xs text-slate-500">
                  {subscriptionUpdate.billing_cycle === 'yearly' 
                    ? 'Leave blank to auto-set 1 year from now' 
                    : 'Leave blank to auto-set 30 days from now'}
                </p>
              </div>
              
              {/* Price Override */}
              <div className="space-y-2">
                <Label className="text-slate-300">Price Override (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Leave blank for default pricing"
                  value={subscriptionUpdate.price_override}
                  onChange={(e) => setSubscriptionUpdate({...subscriptionUpdate, price_override: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-slate-300">Admin Notes (optional)</Label>
                <Textarea
                  placeholder="Reason for change, special agreement, etc."
                  value={subscriptionUpdate.notes}
                  onChange={(e) => setSubscriptionUpdate({...subscriptionUpdate, notes: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white h-20"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowSubscriptionDialog(false)} 
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubscriptionUpdate}
              disabled={updatingSubscription}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {updatingSubscription ? 'Updating...' : 'Update Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Announcement Dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-amber-400" />
              {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingAnnouncement 
                ? 'Update the announcement details below.'
                : 'This announcement will be shown as a popup to all users (excluding demo accounts).'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Title *</Label>
              <Input
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                placeholder="e.g., New Feature: Mobile App Coming Soon!"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Message *</Label>
              <Textarea
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                placeholder="Write your announcement message here..."
                rows={4}
                className="bg-slate-700 border-slate-600 text-white resize-none"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Priority</Label>
                <Select 
                  value={announcementForm.priority} 
                  onValueChange={(value) => setAnnouncementForm({...announcementForm, priority: value})}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="normal" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-blue-400" />
                        Normal
                      </div>
                    </SelectItem>
                    <SelectItem value="important" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-amber-400" />
                        Important
                      </div>
                    </SelectItem>
                    <SelectItem value="urgent" className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-400" />
                        Urgent
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Expires (Optional)</Label>
                <Input
                  type="date"
                  value={announcementForm.expires_at}
                  onChange={(e) => setAnnouncementForm({...announcementForm, expires_at: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAnnouncementDialog(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAnnouncement}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {editingAnnouncement ? 'Update Announcement' : 'Send Announcement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Template Editor Dialog with Live Preview */}
      <Dialog open={showEmailTemplateDialog} onOpenChange={setShowEmailTemplateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="h-5 w-5 text-cyan-400" />
              {isEditingDefaultTemplate ? 'Edit Default Template' : 'Edit Set in Stone Template'}
              {currentEmailTemplate && ` - ${getTemplateTypeLabel(currentEmailTemplate.template_type)}`}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {isEditingDefaultTemplate 
                ? 'This template is used by all companies without white-label subscription.'
                : 'This template overrides the default for Set in Stone only.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6 py-4 h-[60vh]">
            {/* Left side - Editor */}
            <div className="space-y-4 overflow-y-auto pr-4">
              {/* Available Variables */}
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-xs font-medium text-slate-400 mb-2">Click to copy variable:</p>
                <div className="flex flex-wrap gap-1">
                  {(emailTemplateVariables[emailTemplateForm.template_type] || []).map(variable => (
                    <Badge 
                      key={variable} 
                      variant="outline" 
                      className="text-xs cursor-pointer border-slate-500 text-slate-300 hover:bg-cyan-600/30 hover:border-cyan-400"
                      onClick={() => {
                        navigator.clipboard.writeText(`{${variable}}`);
                        toast.success(`Copied {${variable}}`);
                      }}
                    >
                      {'{' + variable + '}'}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300 font-medium">Email Subject</Label>
                <Input
                  value={emailTemplateForm.subject}
                  onChange={(e) => setEmailTemplateForm({...emailTemplateForm, subject: e.target.value})}
                  placeholder="e.g., Your Quote from {company_name}"
                  className="bg-slate-700 border-slate-600 text-white font-medium"
                />
              </div>
              
              <div className="space-y-2 flex-1">
                <Label className="text-slate-300 font-medium">Email Body</Label>
                <Textarea
                  value={emailTemplateForm.body}
                  onChange={(e) => setEmailTemplateForm({...emailTemplateForm, body: e.target.value})}
                  placeholder="Write your email content here..."
                  rows={14}
                  className="bg-slate-700 border-slate-600 text-white font-mono text-sm resize-none"
                />
              </div>
            </div>
            
            {/* Right side - Live Preview */}
            <div className="border border-slate-600 rounded-lg overflow-hidden bg-slate-900 flex flex-col">
              <div className="bg-slate-700 px-4 py-2 border-b border-slate-600 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">Live Preview</span>
                <Badge className="bg-cyan-600 text-xs">Sample Data</Badge>
              </div>
              <div className="flex-1 overflow-y-auto p-4 bg-white">
                {/* Email Preview Container */}
                <div className="bg-white rounded-lg max-w-xl mx-auto">
                  {/* Email Header with Logo */}
                  <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-center gap-4">
                      {companyBranding.logo_url ? (
                        <img 
                          src={companyBranding.logo_url} 
                          alt="Company Logo" 
                          className="h-16 w-auto object-contain"
                        />
                      ) : (
                        <div className="h-16 w-16 bg-slate-200 rounded-lg flex items-center justify-center">
                          <Building2 className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-semibold text-slate-900">
                          {companyBranding.company_name || 'Your Company Name'}
                        </h2>
                        {companyBranding.tagline && (
                          <p className="text-sm text-slate-500">{companyBranding.tagline}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Email Subject Preview */}
                  <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Subject:</p>
                    <p className="font-semibold text-slate-900">
                      {generateEmailPreview().subject || 'Email Subject'}
                    </p>
                  </div>
                  
                  {/* Email Body Preview */}
                  <div className="p-6">
                    <div 
                      className="text-slate-700 leading-relaxed"
                      style={{ fontFamily: 'Georgia, serif', fontSize: '15px' }}
                      dangerouslySetInnerHTML={{ __html: generateEmailPreview().body || '<p class="text-slate-400">Email body will appear here...</p>' }}
                    />
                  </div>
                  
                  {/* Email Footer */}
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 text-center">
                    <div className="text-sm text-slate-600 space-y-1">
                      <p className="font-medium">{companyBranding.company_name}</p>
                      <div className="text-slate-500">
                        {companyBranding.company_email}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2 border-t border-slate-700 pt-4">
            <div className="flex items-center gap-2 mr-auto text-xs text-slate-500">
              <AlertTriangle className="w-4 h-4" />
              Preview shows sample data - actual emails use real order details
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowEmailTemplateDialog(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={saveEmailTemplate}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* China Quote Dialog */}
      <Dialog open={showChinaQuoteDialog} onOpenChange={setShowChinaQuoteDialog}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ship className="w-5 h-5 text-cyan-400" />
              {selectedChinaOrder?.status === 'submitted' ? 'Send Quote' : 'Order Details'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedChinaOrder?.reference_number} - {selectedChinaOrder?.company_name}
            </DialogDescription>
          </DialogHeader>

          {selectedChinaOrder && (
            <div className="space-y-4 py-4">
              {/* Stone Details */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-2">Stone Details</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Colour:</span>
                    <span className="ml-1 text-white">{selectedChinaOrder.stone_colour}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Type:</span>
                    <span className="ml-1 text-white">{selectedChinaOrder.stone_type || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Finish:</span>
                    <span className="ml-1 text-white">{selectedChinaOrder.stone_finish || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Components */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-2">Components</h4>
                <div className="space-y-2">
                  {selectedChinaOrder.components?.map((comp, idx) => (
                    <div key={idx} className="p-2 bg-slate-600/50 rounded flex justify-between items-center">
                      <span className="text-white">
                        {comp.component_type === 'custom' ? comp.custom_description : comp.component_type}
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-cyan-500/30 text-cyan-200 text-xs font-semibold">× {comp.quantity || 1}</span>
                      </span>
                      <span className="text-slate-300 text-sm">
                        {comp.height}" × {comp.width}" × {comp.depth}"
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer Notes */}
              {selectedChinaOrder.customer_notes && (
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <h4 className="font-semibold text-cyan-400 mb-2">Customer Notes</h4>
                  <p className="text-slate-300 text-sm">{selectedChinaOrder.customer_notes}</p>
                </div>
              )}

              {/* Quote Form - Show for submitted/quoted orders */}
              {['submitted', 'quoted'].includes(selectedChinaOrder.status) && (
                <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-500/50">
                  <h4 className="font-semibold text-purple-400 mb-3">Quote Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Supplier Cost (£) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={chinaQuoteForm.china_cost}
                        onChange={(e) => setChinaQuoteForm(prev => ({ ...prev, china_cost: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., 450.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Markup (£) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={chinaQuoteForm.markup_amount}
                        onChange={(e) => setChinaQuoteForm(prev => ({ ...prev, markup_amount: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., 150.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Shipping Allocation (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={chinaQuoteForm.shipping_cost}
                        onChange={(e) => setChinaQuoteForm(prev => ({ ...prev, shipping_cost: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., 100.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Total Quote</Label>
                      <div className="px-3 py-2 bg-slate-600 rounded-md text-xl font-bold text-green-400">
                        £{((parseFloat(chinaQuoteForm.china_cost) || 0) + (parseFloat(chinaQuoteForm.markup_amount) || 0) + (parseFloat(chinaQuoteForm.shipping_cost) || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <Label className="text-slate-300">Quote Notes (optional)</Label>
                    <Textarea
                      value={chinaQuoteForm.quote_notes}
                      onChange={(e) => setChinaQuoteForm(prev => ({ ...prev, quote_notes: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Any notes about the quote..."
                      rows={2}
                    />
                  </div>
                </div>
              )}

              {/* Order Status - Show for accepted+ orders */}
              {!['submitted', 'quoted', 'cancelled'].includes(selectedChinaOrder.status) && (
                <div className="p-4 bg-green-900/30 rounded-lg border border-green-500/50">
                  <h4 className="font-semibold text-green-400 mb-3">Order Status</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Quote:</span>
                      <span className="ml-1 text-white">£{parseFloat(selectedChinaOrder.quote_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Deposit:</span>
                      <span className={`ml-1 ${selectedChinaOrder.deposit_paid ? 'text-green-400' : 'text-amber-400'}`}>
                        {selectedChinaOrder.deposit_paid ? '✓ Paid' : 'Pending'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400">Balance:</span>
                      <span className={`ml-1 ${selectedChinaOrder.balance_paid ? 'text-green-400' : 'text-amber-400'}`}>
                        {selectedChinaOrder.balance_paid ? '✓ Paid' : 'Pending'}
                      </span>
                    </div>
                    {selectedChinaOrder.eta && (
                      <div>
                        <span className="text-slate-400">ETA:</span>
                        <span className="ml-1 text-white">{new Date(selectedChinaOrder.eta).toLocaleDateString('en-GB')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button variant="outline" onClick={() => setShowChinaQuoteDialog(false)} className="text-slate-300 border-slate-600">
              Cancel
            </Button>
            {selectedChinaOrder && ['submitted', 'quoted'].includes(selectedChinaOrder.status) && (
              <Button 
                onClick={handleSendChinaQuote} 
                disabled={submittingChinaQuote}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {submittingChinaQuote ? 'Sending...' : selectedChinaOrder.status === 'quoted' ? 'Update Quote' : 'Send Quote'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
